import { TextLinesIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { getScoreColorVariant, serializeScore } from '@vizit/utils';
import { ScoreDiffIndicator } from '../score-diff-indicator';
import { Skeleton } from '../skeleton';
import {
  informationalIconSizes,
  informationalStickerClasses,
  scoreTextClasses,
  sizeClasses,
  stickerClasses,
} from './styles';
import { type ScoreStickerV2Props, ScoreStickerV2Size } from './types';

/**
 * Score sticker displays a score on a color-coded background.
 *
 * Sizes: Lg (106px) renders an optional diff badge; Sm (64px, default) and
 * Xs (40px) render the score only.
 *
 * The `isInformational` variant replaces the score with a text-lines icon on a
 * neutral gray background, for assets that are not scored on visual power.
 *
 * @example
 * <ScoreStickerV2 score={38} size={ScoreStickerV2Size.Lg} scoreDiff={11} />
 */
function ScoreStickerV2(props: ScoreStickerV2Props) {
  const {
    score,
    scoreDiff,
    size = ScoreStickerV2Size.Sm,
    isLoading = false,
    isProcessing = false,
    isDisabled = false,
    isInformational = false,
    className,
    ref,
    ...other
  } = props;

  const scoreLabel = serializeScore(score);
  const colorVariant = getScoreColorVariant(score);
  const isNa = score == null;
  const showDiff = size === ScoreStickerV2Size.Lg;

  if (isLoading) {
    return (
      <div
        ref={ref}
        className={cx(sizeClasses[size], className)}
        aria-label={`Score: ${scoreLabel}`}
        aria-busy="true"
        data-testid="score-sticker-v2"
        {...other}
      >
        <Skeleton className="h-full w-full" variant="square" />
      </div>
    );
  }

  if (isInformational) {
    return (
      <div
        ref={ref}
        className={cx(informationalStickerClasses({ size }), isProcessing && 'opacity-40', className)}
        aria-label="Informational"
        data-testid="score-sticker-v2"
        {...other}
      >
        <TextLinesIcon className="text-gray-utility-700" size={informationalIconSizes[size]} aria-hidden />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cx(
        stickerClasses({ colorVariant, size, isDisabled, isProcessing }),
        isProcessing && 'opacity-40',
        className,
      )}
      aria-label={`Score: ${scoreLabel}`}
      data-testid="score-sticker-v2"
      {...other}
    >
      <span className={scoreTextClasses({ colorVariant, size, isNa, isDisabled })}>{scoreLabel}</span>
      {showDiff && scoreDiff != null && (
        <ScoreDiffIndicator
          scoreDiff={scoreDiff}
          className={cx('absolute bottom-5 left-1/2 -translate-x-1/2', isDisabled && 'opacity-20')}
        />
      )}
    </div>
  );
}

export { ScoreStickerV2 };
export { ScoreStickerV2Size } from './types';
export type { ScoreStickerV2Props };
